import React, { useEffect, useRef, useState } from "react";
import { Formik } from "formik";
import { bool, func, node, object, string } from "prop-types";

import { useDrawerContext } from "../../utilities/drawerHelpers";
import FormLoading from "../FormLoading";
import Step from "./Step";
import * as S from "./styled";

const MultiStepForm = ({
  children,
  className,
  formikProps,
  initialValues,
  isModal,
  onCancel,
  onSubmit
}) => {
  const hasDrawerForm = useDrawerContext();
  const [stepNumber, setStepNumber] = useState(0);
  const steps = React.Children.toArray(children);
  const [snapshot, setSnapshot] = useState(initialValues);
  const [step, setStep] = useState(steps[stepNumber]);
  const totalSteps = useRef(steps.length);

  useEffect(() => {
    const nextStep = steps[stepNumber];
    if (nextStep) {
      setStep(nextStep);
    }
  }, [stepNumber]);

  useEffect(() => {
    totalSteps.current = steps.length;
  }, [children]);

  const next = values => {
    setSnapshot(values);
    setStepNumber(Math.min(stepNumber + 1, totalSteps.current - 1));
  };

  const handleSubmit = async (values, bag) => {
    if (step.props.onSubmit) {
      const submission = await step.props.onSubmit(values, bag);

      if (submission?.error) {
        return false;
      }
    }

    // this must be done after the onSubmit handling otherwise we
    // may skip a step if onSubmit causes a new step to be added
    const isLastStep = stepNumber === totalSteps.current - 1;

    if (isLastStep) {
      return onSubmit(values, bag);
    }

    bag.setTouched({});
    next(values);

    return true;
  };

  return (
    <Formik {...formikProps} initialValues={snapshot} onSubmit={handleSubmit}>
      <S.Form
        $hasDrawerForm={hasDrawerForm}
        className={className}
        data-testid="experts-contact-box-form"
      >
        {step ? (
          <Step
            isModal={isModal}
            onCancel={onCancel}
            showDrawerButtons={step.props.showDrawerButtons}
          >
            {step}
          </Step>
        ) : (
          <FormLoading isModal={isModal} />
        )}
      </S.Form>
    </Formik>
  );
};

MultiStepForm.propTypes = {
  children: node,
  className: string,
  // eslint-disable-next-line react/forbid-prop-types
  formikProps: object,
  // eslint-disable-next-line react/forbid-prop-types
  initialValues: object,
  isMobile: bool,
  isModal: bool,
  onCancel: func,
  onSubmit: func.isRequired
};

MultiStepForm.defaultProps = {
  children: null,
  className: "",
  formikProps: null,
  initialValues: {},
  isMobile: false,
  isModal: false,
  onCancel: () => null
};

export default MultiStepForm;
